import React, { useEffect, useState } from 'react'
import { Alert, Button, LinearProgress, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import moment from 'moment'
import API from '../../../services/API'
import PermissionCheck from '../../../helpers/PermissionCheck'
import BigModal from '../../../components/Modals/BigModal'
import RegistrationStatusChip from '../components/RegistrationStatusChip'
import ActivityFormDialog from './ActivityFormDialog'

const ACTIVITY_TYPE_LABEL = {
  treatment_of_disease_disorder_injury: 'Treatment of disease, disorder or injury (TDDI)',
  transport_services_triage_medical_advice: 'Transport services, triage and medical advice',
}

// `embedded` renders the tracker without its page title/intro (for use
// inside the CQC Compliance Questions page); `onChanged` fires after any
// add/edit/remove so the host page can refresh derived state.
const ActivitiesList = ({ embedded = false, onChanged }) => {
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [showForm, setShowForm] = useState(false)

  const load = () => {
    setLoading(true)
    API.get('/registration/activities')
      .then((res) => {
        if (res.status === 200) setActivities(res.data.activities)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const remove = (activity) => {
    if (!window.confirm(`Remove "${activity.locationLabel}" from your regulated activities?`)) return
    API.delete(`/registration/activities/${activity._id}`).then((res) => {
      if (res.status === 200) {
        load()
        onChanged?.()
      }
    })
  }

  return (
    <PermissionCheck roles={['Governance']} showAlert>
      <Stack gap={2}>
        <Stack direction="row" justifyContent={embedded ? 'flex-end' : 'space-between'} alignItems="center">
          {!embedded && <Typography variant="title1">Regulated Activities</Typography>}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null)
              setShowForm(true)
            }}
          >
            Add Activity
          </Button>
        </Stack>

        {!embedded && (
          <Alert severity="info" variant="outlined">
            Track what your organisation is actually CQC-registered for, by activity and location. This
            is separate from CQC Centre's inspection-readiness evidence, which assumes registration
            already exists.
          </Alert>
        )}

        {loading && <LinearProgress />}
        {!loading && activities.length === 0 && (
          <Alert severity="warning">
            No regulated activities recorded yet. Add one to start tracking your registration status.
          </Alert>
        )}

        <Stack gap={1}>
          {activities.map((activity) => (
            <Stack
              key={activity._id}
              direction={{ xs: 'column', sm: 'row' }}
              gap={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <Stack>
                <Typography variant="body2" fontWeight={700}>
                  {activity.locationLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {ACTIVITY_TYPE_LABEL[activity.activityType] || activity.activityType}
                  {activity.registeredSince &&
                    ` - registered since ${moment(activity.registeredSince).format('Do MMM YYYY')}`}
                </Typography>
              </Stack>
              <Stack direction="row" gap={1} alignItems="center">
                <RegistrationStatusChip status={activity.registrationStatus} />
                <Button
                  size="small"
                  onClick={() => {
                    setEditing(activity)
                    setShowForm(true)
                  }}
                >
                  Edit
                </Button>
                <Button size="small" color="error" onClick={() => remove(activity)}>
                  Remove
                </Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <BigModal open={showForm} onClose={() => setShowForm(false)}>
        <ActivityFormDialog
          activity={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false)
            load()
            onChanged?.()
          }}
        />
      </BigModal>
    </PermissionCheck>
  )
}

export default ActivitiesList
